import type { NormalizedMedia, UploadableMedia } from '@evertel/media'
import { APIDataMedia } from '@evertel/types'
import { IconName } from '@fortawesome/fontawesome-svg-core'
import { localId } from './general'

//==============================================
// DETERMINES ORIENTATION FROM PHOTO DATA
//==============================================
export function determineOrientation(
    photoFile: any,
    fallbackWidth: number,
    fallbackHeight: number
): 'landscape' | 'portrait' {

    //"orientation" is intentionally ALL LOWERCASE
    if (['landscape', 'portrait'].includes(photoFile.metadata?.orientation)) {
        //this field only exists on our already processed media, so if it exists, use its value
        return photoFile.metadata?.orientation
    }

    // 1. Check iOS EXIF orientation
    if (photoFile.metadata?.Orientation) {
        // in iOS we get photoFile.orientation as well as the metadata.Orientation
        // but the photoFile.orientation is wrong, so we must use metadata.Orientation first

        // EXIF orientation 5, 6, 7, 8 are portrait orientations
        if ([5, 6, 7, 8].includes(photoFile.metadata.Orientation)) {
            return 'portrait'
        }
        // EXIF orientation 1, 2, 3, 4 are landscape orientations
        if ([1, 2, 3, 4].includes(photoFile.metadata.Orientation)) {
            return 'landscape'
        }
    }

    // 2. Check direct orientation from PhotoFile
    if (photoFile.orientation) {
        if (photoFile.orientation.includes('portrait')) return 'portrait'
        if (photoFile.orientation.includes('landscape')) return 'landscape'
    }

    // 3. Fallback to dimension comparison
    return fallbackWidth > fallbackHeight ? 'landscape' : 'portrait'
}

//==============================================
// CORRECTS IMAGE DIMENSIONS BASED ON ORIENTATION
//==============================================
export function correctImageDimensions(
    photoFile: any
): { width: number, height: number } {
    const orientation = determineOrientation(photoFile, photoFile.width, photoFile.height)

    if (orientation === 'portrait') {
        return {
            // in portrait mode, the width is always smaller
            width: Math.min(photoFile.width, photoFile.height),
            height: Math.max(photoFile.width, photoFile.height)
        }
    }
    // default to landscape where width is always greater than height
    return {
        width: Math.max(photoFile.width, photoFile.height),
        height: Math.min(photoFile.width, photoFile.height)
    }
}

//==============================================
// GETS IMAGE DIMENSIONS <Promise>
//==============================================
export async function getImageDimensions(url: string): Promise<{ width: number, height: number }> {
    const img = new Image()

    const loadImage = (): Promise<{ width: number, height: number }> => {
        return new Promise((resolve, reject) => {
            const onLoad = () => {
                resolve({ width: img.naturalWidth, height: img.naturalHeight })
            }

            const onError = (err: Event) => {
                reject(err)
            }

            img.addEventListener('load', onLoad, { once: true })
            img.addEventListener('error', onError, { once: true })

            img.src = url
        })
    }
    return await loadImage()
}

//==============================================
// RETURNS FILE EXTENSION FROM URL
//==============================================
export function getFileExtensionFromUrl(url: string): string | undefined {
    // i.e. https://example.com/fold.er/fil.e.jpg?param.eter#hash=12.345 returns jpg
    return url.split(/[#?]/)[0].split('.').pop().trim()
}

//==============================================
// RETURNS TYPE OF MEDIA FROM MIMETYPE OR MEDIA OBJECT
//==============================================
export function getMediaType(media: APIDataMedia | NormalizedMedia | string, heicSupport = false): string {

    // Get mime type from either string or object

    const mimeType = typeof media === 'string'
        ? media
        : (media && typeof media === 'object')
            // @ts-expect-error multi-type ignore
            ? (media.mimeType || media.mimetype || '')
            : ''

    if (!mimeType) {
        return 'application'
    }

    // Only override HEIC/HEIF to 'application' if heicSupport is false
    if (!heicSupport && (mimeType.includes('heic') || mimeType.includes('heif'))) {
        return 'application'
    }
    // Extract media type from mime type
    return mimeType.split('/')[0] || 'application'

}

//==============================================
// SORTS MEDIA FOR DISPLAY
//==============================================
export const sortMediaByType = (mediaItems: NormalizedMedia[]) => {
    // Create three sorted groups
    const files = mediaItems.filter(m => !['audio', 'image', 'video'].includes(m.mediaType))
    const audio = mediaItems.filter(m => m.mediaType === 'audio')
    const visualMedia = mediaItems.filter(m => ['image', 'video'].includes(m.mediaType))

    // Return the concatenated arrays in the desired order
    return [...files, ...audio, ...visualMedia]
}

//==============================================
// RETURNS SCALE FLOAT BASED ON ASPECT RATIO
//==============================================
export function parseAspectRatio(aspectRatio: string): number {
    // returns aspect ratio multiplier from string (ie 4:3 will return 1.333333)
    let [p1, p2]: string[] | number[] = aspectRatio.split(':')
    p1 = parseInt(p1, 10)
    p2 = parseInt(p2, 10)
    return p1 / p2
}

//==============================================
// SCALES MEDIA BASED ON MAX DIMENSIONS AND
// NATURAL ASPECT RATIO
//==============================================
interface scaleMediaProps {
    maxWidth: number
    maxHeight: number
    naturalWidth: number
    naturalHeight: number
}
export function scaleMedia({ maxWidth, maxHeight, naturalWidth, naturalHeight }: scaleMediaProps) {
    let width = naturalWidth
    let height = naturalHeight

    // check if the current width is larger than the max
    if (naturalWidth > maxWidth) {
        const ratio = maxWidth / naturalWidth   // get aspect ratio multiplier
        height = naturalHeight * ratio   // scale height
        width = naturalWidth * ratio    // scale width
    }

    // if the scaled height is still larger than max scale some more
    if (height > maxHeight) {
        const ratio = maxHeight / height   // get aspect ratio multiplier
        width = width * ratio   // scale width
        height = height * ratio   // scale height
    }

    return { width: Math.round(width), height: Math.round(height) }
}

//get media Model from clipboard string
export function getClipboardMedia(clipboardString: string): APIDataMedia {
    const fileName = filenameFromPath(clipboardString)
    const extension = clipboardString.split('.').pop() || ''
    const mimeType = mimeTypeFromExtension(extension)

    const clipboardMedia = {
        uri: clipboardString,
        fileName: fileName,
        type: extension,
        mimetype: mimeType
    }
    return clipboardMedia
}

//==============================================
// RETURNS MIMETYPE FROM FILE EXTENSION
//==============================================
export function mimeTypeFromExtension(extension: string): string {
    if (!extension) return 'application/octet-stream'

    switch (extension.toLowerCase()) {
        // Image formats
        case 'jpeg':
        case 'jpg':
        case 'jpe':
            return 'image/jpeg'
        case 'png':
            return 'image/png'
        case 'gif':
            return 'image/gif'
        case 'webp':
            return 'image/webp'
        case 'svg':
            return 'image/svg+xml'
        case 'heic':
            return 'image/heic'
        case 'heif':
            return 'image/heif'
        case 'bmp':
            return 'image/bmp'
        case 'tiff':
        case 'tif':
            return 'image/tiff'
        case 'avif':
            return 'image/avif'
        case 'ico':
            return 'image/x-icon'
        case 'psd':
            return 'image/vnd.adobe.photoshop'

        // Video formats
        case 'mp4':
        case 'mp4v':
        case 'mpg4':
        case 'm4v':
            return 'video/mp4'
        case 'mpeg':
        case 'mpg':
        case 'mpe':
        case 'm1v':
        case 'm2v':
            return 'video/mpeg'
        case 'mov':
            return 'video/quicktime'
        case 'webm':
            return 'video/webm'
        case 'avi':
            return 'video/x-msvideo'
        case 'mkv':
            return 'video/x-matroska'
        case 'flv':
            return 'video/x-flv'
        case 'wmv':
            return 'video/x-ms-wmv'
        case '3gp':
            return 'video/3gpp'

        // Audio formats
        case 'm4a':
            return 'audio/m4a'
        case 'aac':
            return 'audio/aac'
        case 'mp3':
            return 'audio/mpeg'
        case 'wav':
            return 'audio/wav'
        case 'wma':
            return 'audio/x-ms-wma'
        case 'mid':
        case 'midi':
            return 'audio/midi'
        case 'mpa':
            return 'audio/mpeg'
        case 'ogg':
            return 'audio/ogg'

            // Microsoft Office formats

        case 'doc':
            return 'application/msword'
        case 'docx':
            return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
        case 'xls':
            return 'application/vnd.ms-excel'
        case 'xlsx':
            return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        case 'xlsm':
            return 'application/vnd.ms-excel.sheet.macroEnabled.12'
        case 'ppt':
            return 'application/vnd.ms-powerpoint'
        case 'pptx':
            return 'application/vnd.openxmlformats-officedocument.presentationml.presentation'
        case 'pps':
            return 'application/vnd.ms-powerpoint'
        case 'pptsx':
            return 'application/pptsx'

        // Apple iWork formats
        case 'pages':
            return 'application/vnd.apple.pages'
        case 'numbers':
            return 'application/vnd.apple.numbers'
        case 'key':
            return 'application/vnd.apple.keynote'

        // OpenDocument formats
        case 'ods':
            return 'application/vnd.oasis.opendocument.spreadsheet'
        case 'odp':
            return 'application/vnd.oasis.opendocument.presentation'

        // Archive/Compression formats
        case 'zip':
            return 'application/zip'
        case 'rar':
            return 'application/vnd.rar'
        case 'tgz':
            return 'application/x-compressed-tar'
        case '7z':
            return 'application/x-7z-compressed'
        case 'z':
            return 'application/x-compress'

        // Data/Database formats
        case 'csv':
            return 'text/csv'
        case 'xml':
            return 'application/xml'
        case 'sql':
            return 'application/sql'
        case 'db':
            return 'application/x-sqlite3'
        case 'dbf':
            return 'application/x-dbase'

        // Text formats
        case 'txt':
            return 'text/plain'
        case 'rtf':
            return 'application/rtf'
        case 'wpd':
            return 'application/vnd.wordperfect'

        // Other formats
        case 'pdf':
            return 'application/pdf'

        default:
            return 'application/octet-stream'
    }
}

//==============================================
// RETURNS FILENAME FROM FULL FILE PATH
//==============================================
export function filenameFromPath(filePath: string): string | undefined {
    // returns file name from passed in file path string
    if (filePath && typeof filePath === 'string') {
        return filePath.split('\\').pop().split('/').pop()
    }
    return undefined
}

//==============================================
// RETURNS FILE EXTENSION FROM FILENAME OR
// MEDIA OBJECT
//==============================================
export function getFileExt(file: string | APIDataMedia | File): string | undefined {
    if (!file) return undefined

    // returns file extention from file name string or full media object
    let el = null
    if (typeof file === 'string') {
        el = file
    } else if ('name' in file) {
        el = file.name
    } else if ('fileName' in file) {
        el = file.fileName
    }

    if (!el) return undefined

    return el.split('.').pop()
}

//==============================================
// RETURNS FILE ICON NAME, COLOR, TYPENAME
// FOR THE FILE PASSED IN
//==============================================
interface Attributes {
    iconName: IconName
    iconColor: string
    typeName: string
}
export function getFileAttributes(fileName: string): Attributes {
    const fileType = getFileExt(fileName)?.toLowerCase()
    const attributes: Attributes = { iconName: 'file', iconColor: 'secondary', typeName: `${fileType} File` }

    switch (fileType) {
        case 'jpg':
        case 'jpeg':
        case 'png':
        case 'gif':
        case 'bmp':
        case 'svg':
        case 'webp':
        case 'ico':
        case 'tiff':
        case 'psd':
        case 'heic':
        case 'heif':
            attributes.iconName = 'file-image'
            attributes.iconColor = '#FF9800' // Image Orange
            attributes.typeName = 'Image File'
            break

        case 'mp3':
        case 'm4a':
        case 'wav':
        case 'aac':
        case 'aif':
        case 'cda':
        case 'mid':
        case 'midi':
        case 'mpa':
        case 'ogg':
        case 'wma':
        case 'wpl':
        case 'mkv':
            attributes.iconName = 'file-audio'
            attributes.iconColor = '#00BCD4' // Audio Green
            attributes.typeName = 'Audio File'
            break

        case 'mp4':
        case 'avi':
        case 'mpg':
        case 'mov':
        case 'webm':
        case '3gp':
        case 'wmv':
        case 'm4v':
            attributes.iconName = 'file-video'
            attributes.iconColor = '#E91E63' // Video Purple
            attributes.typeName = 'Video File'
            break

        case 'xlsx':
        case 'xls':
        case 'xlsm':
        case 'ods':
        case 'numbers':
            attributes.iconName = 'file-excel'
            attributes.iconColor = '#217346' // Excel Green
            attributes.typeName = 'Excel Spreadsheet'
            break

        case 'pdf':
            attributes.iconName = 'file-pdf'
            attributes.iconColor = '#FF0000' // Adobe Red
            attributes.typeName = 'PDF Document'
            break

        case 'doc':
        case 'docx':
        case 'pages':
            attributes.iconName = 'file-word'
            attributes.iconColor = '#2B579A' // Word Blue
            attributes.typeName = 'Word Document'
            break

        case 'zip':
        case 'rar':
        case 'tgz':
        case 'tar.gz':
        case 'z':
        case '7z':
            attributes.iconName = 'file-archive'
            attributes.iconColor = '#FFC107' // Archive Yellow/Gold
            attributes.typeName = 'File Archive'
            break

        case 'ppt':
        case 'pptx':
        case 'pptsx':
        case 'pps':
        case 'key':
        case 'odp':
            attributes.iconName = 'file-powerpoint'
            attributes.iconColor = '#D24726' // PowerPoint Orange
            attributes.typeName = 'PowerPoint Presentation'
            break

        case 'csv':
            attributes.iconName = 'file-csv'
            attributes.iconColor = '#34A853' // CSV Light Green
            attributes.typeName = 'CSV File'
            break

        case 'db':
        case 'dbf':
        case 'sql':
        case 'xml':
            attributes.iconName = 'database'
            attributes.iconColor = '#6A1B9A' // Database Purple
            attributes.typeName = 'Data File'
            break

        case 'txt':
        case 'wpd':
        case 'rtf':
            attributes.iconName = 'file-alt'
            attributes.iconColor = '#616161' // Neutral Grey
            attributes.typeName = 'Text File'
            break

        default:
            attributes.iconColor = '#9E9E9E' // Default Grey
            break
    }
    return attributes

}

//==============================================
// RETURNS A VIDEO'S META DATA <Promise>
//==============================================
export async function getVideoMetadata(url: string): Promise<{ height: number, width: number, duration: number }> {
    const video = document.createElement('video')

    const loadVideoMetadata = (): Promise<{ height: number, width: number, duration: number }> => {
        return new Promise((resolve, reject) => {
            const onLoadedMetadata = () => {
                resolve({
                    height: video.videoHeight,
                    width: video.videoWidth,
                    duration: video.duration * 1000
                })
            }

            const onError = (err: Event) => {
                console.log('ERROR GETTING VIDEO METADATA', err)
                reject(err)
            }

            video.addEventListener('loadedmetadata', onLoadedMetadata, { once: true })
            video.addEventListener('error', onError, { once: true })

            video.src = url
        })
    }

    const metadata = await loadVideoMetadata()
    return metadata

}

//==============================================
// TESTS IF AN IMAGE URL IS VALID <Promise>
//==============================================
export function testImage(url: string, timeoutT?: number) {
    return new Promise(function (resolve, reject) {
        const timeout = timeoutT || 3000
        const img = new Image()
        let timer = undefined

        img.onerror = img.onabort = function () {
            clearTimeout(timer)
            reject('error')
        }
        img.onload = function () {
            clearTimeout(timer)
            resolve('success')
        }
        timer = setTimeout(function () {
            // reset .src to invalid URL so it stops previous
            // loading, but doens't trigger new load
            img.src = '//!!!!/noexist.jpg'
            reject('timeout')
        }, timeout)

        img.src = url
    })
}

//==============================================
// RETURNS FILEPATH NAME FROM FULL FILEPATH
//==============================================
export function getFilePathName(filePath: string, seperator = '/'): string {
    return filePath.substring(0, filePath.lastIndexOf(seperator))
}

//==============================================
// RETURNS FILEPATH BASE FROM FULL FILEPATH
//==============================================
export function getFileBaseName(str: string, seperator = '/'): string {
    return str.substring(str.lastIndexOf(seperator) + 1)
}

//==============================================
// MOBILE ONLY! HELPER TO
// GETDISTANCEBETWEENTOUCHES()
//==============================================
export function pow2abs(a: number, b: number): number {
    // used as helper to get the distance between two touches on mobile
    return Math.pow(Math.abs(a - b), 2)
}

//==============================================
// WEB ONLY!
// fileToUploadableMedia()
//==============================================
export const fileToUploadableMedia = async (file: File, heicSupport = false): Promise<UploadableMedia> => {
    const url = URL.createObjectURL(file)

    const mimeType = file.type || 'application/unknown'
    const mediaType = getMediaType(mimeType, heicSupport)

    let width: number, height: number, duration: number

    if (mediaType === 'image') {
        const imageDimensions = await getImageDimensions(url)
        width = imageDimensions.width
        height = imageDimensions.height
    }

    if (mediaType === 'video') {
        const videoDimensions = await getVideoMetadata(url)
        width = videoDimensions.width
        height = videoDimensions.height
        duration = videoDimensions.duration
    }

    const orientation = (width != null && height != null)
        ? (width > height ? 'landscape' : 'portrait')
        : undefined

    const meta: Record<string, any> = {}
    if (width != null) meta.width = width
    if (height != null) meta.height = height
    if (duration != null) meta.duration = duration
    if (orientation != null) meta.orientation = orientation

    return {
        id: localId(),
        ownerId: undefined,
        url,
        fileName: file.name,
        mediaType: mediaType,
        mimeType,
        contentLength: file.size,
        orientation,
        width,
        height,
        duration,
        meta,
        isLocalMedia: true,
        state: 'readyToUpload',
        uploadProgress: 0,
        onGetPartFormData: async (partNumber, start, end) => {
            const fileData = file.slice(start, end)
            const formData = new FormData()
            formData.append('part', fileData)
            return formData
        },
        updatedDate: new Date()
    }
}

//==============================================
// MOBILE ONLY! RETURNS THE DISTANCE BETWEEN
// TWO TOUCHPOINTS
//==============================================
export function getDistanceBetweenTouches(touches: Array<Touch>) {
    // used for pinch & zoom
    const [a, b] = touches

    if (a == null || b == null) {
        return 0
    }
    return Math.sqrt(
        pow2abs(a.pageX, b.pageX) + pow2abs(a.pageY, b.pageY)
    )
}

//==============================================
// RETURNS THE SCALE OF A ZOOMED IMAGE
//==============================================
export function getScale(currentDistance: number, initialDistance: number): number {
    // get the scale of a zoomed image
    return currentDistance / initialDistance * 1.2
}

//==============================================
// RETURNS THE CLOSEST SIZE VERSION OF AN IMAGE
// BASED ON THE NEEDED HEIGHT
//==============================================
export function getClosestVersionByHeight(requestedHeight: number, media: APIDataMedia | NormalizedMedia) {
    if (!requestedHeight || !media) return undefined

    if (!(media as NormalizedMedia).versions && !media.meta?.versions) return undefined

    // Handle both old and new data structures
    const meta = media.meta || media
    const versions = meta.versions || []

    if (versions.length === 0) return undefined

    // Prefer the smallest version that is >= the requested height (avoids
    // blurry upscaling). If nothing is large enough, fall back to the
    // largest available version.
    const aboveOrEqual = versions.filter(v => v.height >= requestedHeight)

    let closestHeightVersion
    if (aboveOrEqual.length > 0) {
        closestHeightVersion = aboveOrEqual.reduce((a, b) => b.height < a.height ? b : a)
    } else {
        closestHeightVersion = versions.reduce((a, b) => b.height > a.height ? b : a)
    }

    // Return the version, add url like getVersion does
    return {
        ...closestHeightVersion,
        url: `${media.url}?ver=${closestHeightVersion.id}&u=${media.id}`
    }
}

const bannedExtensions = [
    // Windows executables & core system files
    'exe', 'msi', 'com', 'dll', 'scr',
    'cpl', 'msc', 'pif', 'application',

    // Scripts & batch files
    'bat', 'cmd', 'ps1', 'vbs', 'js',
    'wsf', 'hta', 'gadget',

    // Shell scripts
    'sh', 'bash', 'zsh', 'ksh',

    // Programming language scripts
    'py', 'pyc', 'pyw',    // Python
    'php', 'pl', 'rb',     // PHP, Perl, Ruby
    'asp', 'aspx', 'cgi',  // Web scripts
    'jar',                 // Java

    // Macro-enabled Office documents
    'docm', 'xlsm', 'pptm',
    'dotm', 'xltm', 'xlam',

    // Windows system & shortcuts
    'reg',  // Registry files
    'lnk'   // Shortcuts
]

export const isExtensionBanned = (ext: string) => { return bannedExtensions.includes(ext) }
